import React, { useState, forwardRef, useEffect, useId, useRef } from 'react';
import type { InputMask } from 'imask';
import { AppInput } from '../AppInput';
import type { AppInputRef } from '../AppInput';
import type { AppPhoneInputProps } from './AppPhoneInput.types';
import { normalizeRussianPhoneDigits, unmaskedToPhoneDigits, validatePhone } from './utils/phoneHelpers';

/**
 * Специализированный компонент для ввода номера телефона с автоматическим форматированием
 * Использует AppInput с маской для удобного ввода
 * 
 * @example
 * ```tsx
 * <AppPhoneInput
 *   label="Номер телефона"
 *   onPhoneComplete={(phone) => console.log('Complete:', phone)}
 *   onValidate={(isValid, phone) => console.log('Valid:', isValid)}
 * />
 * ```
 */
const AppPhoneInput = forwardRef<AppInputRef, AppPhoneInputProps>(({
  value,
  onChange,
  onBlur,
  onPhoneComplete,
  onValidate,
  validateOnBlur = true,
  error,
  label = 'Номер телефона',
  placeholder = '+7 (___) ___-__-__',
  disabled,
  autoFocus,
  inputProps,
  // countryCode - зарезервировано для будущего использования
  ...restProps
}, ref) => {
  const [internalError, setInternalError] = useState<string>('');
  const instanceId = useId();
  // Последнее корректно посчитанное значение (10 цифр без кода страны).
  // handleBlur получает только нативный FocusEvent, без доступа к maskRef
  // IMask, поэтому не может сам пересчитать unmaskedValue — переиспользует
  // то, что уже посчитали handleAccept/handleComplete/paste.
  const lastCleanPhoneRef = useRef('');

  // IMask не умеет распознавать лишний код страны/федеральный префикс ("+7"/"8")
  // в начале ВСТАВЛЯЕМОГО текста — он просто заполняет цифрами редактируемые
  // слоты маски по порядку и обрезает последнюю цифру, если вставили на одну
  // больше, чем помещается. Перехватываем paste на фазе перехвата (раньше
  // внутреннего обработчика IMask) и сами нормализуем номер. Слушатель на
  // document, а не на обёртке — обёртка с display:contents ломает CSS-правила
  // вида ".parent > *", рассчитанные на то, что инпут — прямой потомок.
  useEffect(() => {
    const handlePasteCapture = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target || target.getAttribute('data-phone-input-id') !== instanceId) return;

      const text = e.clipboardData?.getData('text') ?? '';
      const digits = text.replace(/\D/g, '');
      if (digits.length < 10) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      const normalized = normalizeRussianPhoneDigits(digits);
      lastCleanPhoneRef.current = normalized;
      onChange?.(normalized, {} as React.ChangeEvent<HTMLInputElement>);
      const isValid = validatePhone(normalized);
      onValidate?.(isValid && normalized.length === 10, normalized);
    };

    document.addEventListener('paste', handlePasteCapture, true);
    return () => document.removeEventListener('paste', handlePasteCapture, true);
  }, [instanceId, onChange, onValidate]);

  // ВАЖНО: раньше цифры вытаскивались regex'ом из ОТФОРМАТИРОВАННОЙ строки
  // маски (extractPhoneDigits(maskedValue)), а "код страны" отрезался только
  // если суммарная длина цифр была ровно 11. Пока номер набирается, отформа-
  // тированное значение содержит placeholder-подчёркивания для незаполненных
  // разрядов — на 9 введённых цифрах строка вида "+7 (916) 123-45-6_" после
  // отбрасывания нецифровых символов давала как раз 10 "цифр" (литерал "7" +
  // 9 введённых), и недобранный на одну цифру номер ошибочно проходил
  // валидацию как полный. unmaskedValue у IMask уже даёт ровно то, что
  // реально введено (без плейсхолдеров), поэтому используем его.
  const handleAccept = (_maskedValue: string, maskRef: InputMask) => {
    const cleanPhone = unmaskedToPhoneDigits(maskRef.unmaskedValue ?? '');
    lastCleanPhoneRef.current = cleanPhone;
    onChange?.(cleanPhone, {} as React.ChangeEvent<HTMLInputElement>);
    // Валидируем на каждое изменение, чтобы кнопка блокировалась при удалении цифр
    const isValid = validatePhone(cleanPhone);
    onValidate?.(isValid && cleanPhone.length === 10, cleanPhone);
  };

  const handleComplete = (_maskedValue: string, maskRef: InputMask) => {
    const cleanPhone = unmaskedToPhoneDigits(maskRef.unmaskedValue ?? '');
    lastCleanPhoneRef.current = cleanPhone;
    onPhoneComplete?.(cleanPhone);
    onValidate?.(true, cleanPhone);
    setInternalError('');
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (validateOnBlur) {
      const cleanPhone = lastCleanPhoneRef.current;
      const isValid = validatePhone(cleanPhone);

      if (!isValid && cleanPhone.length > 0) {
        setInternalError('Введите корректный номер телефона');
        onValidate?.(false, cleanPhone);
      } else if (cleanPhone.length > 0) {
        setInternalError('');
        onValidate?.(true, cleanPhone);
      } else {
        // Пустое поле - не показываем ошибку
        setInternalError('');
      }
    }

    onBlur?.(e);
  };

  const mergedInputProps: React.InputHTMLAttributes<HTMLInputElement> = {
    ...inputProps,
    'data-phone-input-id': instanceId,
  } as React.InputHTMLAttributes<HTMLInputElement>;

  return (
    <AppInput
      ref={ref}
      value={value}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      autoFocus={autoFocus}
      error={error || internalError}
      onBlur={handleBlur}
      // Маска для телефона
      mask="+{7} (000) 000-00-00"
      lazy={false}
      placeholderChar="_"
      onAccept={handleAccept}
      onComplete={handleComplete}
      inputProps={mergedInputProps}
      {...restProps}
    />
  );
});

AppPhoneInput.displayName = 'AppPhoneInput';

export default AppPhoneInput;